import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { InfoBanner } from '../../components/ui/info-banner';
import { 
  Calendar, Clock, CheckCircle, XCircle, RefreshCw, Loader2, 
  Plus, Eye, Trash2, Edit, CalendarDays, CalendarRange, Layers,
  User, FileText, AlertCircle, Upload, Download, X, Send, Users,
  ArrowUpDown, ArrowUp, ArrowDown, Archive, RotateCcw
} from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth header helper
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const BranchAssignments = ({ branchIdOverride, readOnly = false }) => {
  const { user } = useAuth();
  const [mainTab, setMainTab] = useState('daily');
  
  // Schedules state
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Okunmamış iş planları sayısı (tab bazlı)
  const getUnreadCount = (type) => {
    return schedules.filter(s => 
      s.schedule_type === type && 
      !s.is_archived && 
      s.owner_id !== user?.id && // Kendi oluşturduğu planlar yeni değil
      !s.read_by?.includes(user?.id)
    ).length;
  };
  
  const unreadDailyCount = getUnreadCount('daily');
  const unreadMonthlyCount = getUnreadCount('monthly');
  const unreadMasterCount = getUnreadCount('master');
  const totalUnreadCount = unreadDailyCount + unreadMonthlyCount + unreadMasterCount;
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [progressLogs, setProgressLogs] = useState([]);
  const [newProgressLog, setNewProgressLog] = useState({ description: '', documents: [] });
  
  // Completion modal state
  const [completionModalOpen, setCompletionModalOpen] = useState(false);
  const [completionData, setCompletionData] = useState({
    completion_notes: '',
    completion_date: new Date().toISOString().split('T')[0]
  });
  
  // Form state
  const [newSchedule, setNewSchedule] = useState({
    title: '',
    description: '',
    schedule_type: 'daily',
    start_date: '',
    end_date: '',
    assigned_to: [],
    priority: 'normal',
    documents: [],
    send_to_users: [],
    visible_to_users: []
  });
  
  // Document upload state
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  // Sorting state - varsayılan: son güncelleme (en yeni üstte)
  const [sortConfig, setSortConfig] = useState({ key: 'updated_at', direction: 'desc' });

  // Arşiv açıklaması modal state'leri
  const [archiveReasonModalOpen, setArchiveReasonModalOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveTargetId, setArchiveTargetId] = useState(null);
  const [archivingItem, setArchivingItem] = useState(false);

  // Çoklu seçim state'leri
  const [selectedSchedules, setSelectedSchedules] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // YENİ Badge için - read_by sistemini kullan

  // Arşiv state
  const [archivedSchedules, setArchivedSchedules] = useState([]);

  useEffect(() => {
    if (mainTab === 'archive') {
      loadArchivedSchedules();
    } else {
      loadSchedules();
    }
    loadEmployees();
    loadUsers();
  }, [mainTab]);

  // Detay modal açıldığında okundu olarak işaretle
  useEffect(() => {
    if (detailModalOpen && selectedSchedule?.id && isScheduleUnread(selectedSchedule)) {
      markScheduleAsRead(selectedSchedule.id);
    }
  }, [detailModalOpen, selectedSchedule]);

  // İş programı okunmamış mı kontrol et - read_by array'inde user.id olup olmadığına bak
  const isScheduleUnread = (schedule) => {
    if (!user?.id) return false;
    // Kendi oluşturduğu programlar yeni değil
    if (schedule.owner_id === user.id) return false;
    return !schedule.read_by?.includes(user.id);
  };

  // İş programını okundu olarak işaretle (mark-read API)
  const markScheduleAsRead = async (scheduleId) => {
    try {
      await axios.post(`${API}/work/schedules/${scheduleId}/mark-read`, {}, { headers: getAuthHeaders() });
      
      // State'i güncelle - programı okundu olarak işaretle
      setSchedules(prev => prev.map(s => 
        s.id === scheduleId 
          ? { ...s, read_by: [...(s.read_by || []), user.id] }
          : s
      ));
      
      // Sidebar sayacını güncelle
      window.dispatchEvent(new Event('unread-counts-updated'));
    } catch (error) {
      console.error('İş programı okundu yapılırken hata:', error);
    }
  };

  const loadSchedules = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/work/schedules`, {
        params: { schedule_type: mainTab },
        headers: getAuthHeaders()
      });
      setSchedules(res.data || []);
    } catch (error) {
      console.error('İş planları yüklenirken hata:', error);
      toast.error('İş planları yüklenemedi');
    }
    setLoading(false);
  };

  // Arşivlenmiş iş planlarını yükle
  const loadArchivedSchedules = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/work/schedules/archived`, {
        headers: getAuthHeaders()
      });
      setArchivedSchedules(res.data || []);
    } catch (error) {
      console.error('Arşivlenmiş iş planları yüklenirken hata:', error);
    }
    setLoading(false);
  };

  // İş planını arşivle
  const openArchiveReasonModal = (scheduleId) => {
    setArchiveTargetId(scheduleId);
    setArchiveReason('');
    setArchiveReasonModalOpen(true);
  };

  const handleConfirmArchiveSchedule = async () => {
    if (!archiveReason.trim()) {
      toast.error('Arşiv açıklaması zorunludur');
      return;
    }
    
    setArchivingItem(true);
    try {
      await axios.delete(`${API}/work/schedules/${archiveTargetId}`, { 
        headers: getAuthHeaders(),
        data: { archive_reason: archiveReason }
      });
      toast.success('İş planı arşivlendi');
      try {
        await loadSchedules();
      } catch (e) {
        console.log('Liste yenileme hatası:', e);
      }
      setArchiveReasonModalOpen(false);
      setArchiveReason('');
      setArchiveTargetId(null);
    } catch (error) {
      console.error('Arşivleme hatası:', error);
      toast.error('İş planı arşivlenemedi');
    } finally {
      setArchivingItem(false);
    }
  };

  const handleArchiveSchedule = async (scheduleId) => {
    openArchiveReasonModal(scheduleId);
  };

  // İş planını arşivden geri yükle
  const handleRestoreSchedule = async (scheduleId) => {
    try {
      const response = await axios.post(`${API}/work/schedules/${scheduleId}/restore`, {}, { headers: getAuthHeaders() });
      if (response.status === 200) {
        toast.success('İş planı arşivden geri yüklendi');
        try {
          await loadArchivedSchedules();
        } catch (e) {
          console.log('Liste yenileme hatası:', e);
        }
      }
    } catch (error) {
      console.error('Geri yükleme hatası:', error);
      toast.error('İş planı geri yüklenemedi');
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await axios.get(`${API}/branch/employees`, { headers: getAuthHeaders() });
      setEmployees(res.data || []);
    } catch (error) {
      console.error('Çalışanlar yüklenirken hata:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/calendar/users-for-share`, { headers: getAuthHeaders() });
      setUsers(res.data.users || []);
    } catch (error) {
      console.error('Kullanıcılar yüklenirken hata:', error);
    }
  };

  // Document upload handler
  const handleDocumentUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setNewSchedule(prev => ({
          ...prev,
          documents: [...prev.documents, {
            name: file.name,
            type: file.type,
            size: file.size,
            data: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeDocument = (index) => {
    setNewSchedule(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const toggleEmployeeSelection = (empId) => {
    setNewSchedule(prev => {
      const users = prev.send_to_users.includes(empId)
        ? prev.send_to_users.filter(id => id !== empId)
        : [...prev.send_to_users, empId];
      return { ...prev, send_to_users: users };
    });
  };

  const toggleUserSelection = (userId) => {
    setNewSchedule(prev => {
      const currentUsers = prev.send_to_users.includes(userId)
        ? prev.send_to_users.filter(id => id !== userId)
        : [...prev.send_to_users, userId];
      return { ...prev, send_to_users: currentUsers };
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Sıralama fonksiyonu
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sıralama ikonu
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-amber-600" />
      : <ArrowDown className="h-4 w-4 text-amber-600" />;
  };

  // Çoklu seçim fonksiyonları
  const toggleScheduleSelection = (scheduleId) => {
    setSelectedSchedules(prev => 
      prev.includes(scheduleId) 
        ? prev.filter(id => id !== scheduleId)
        : [...prev, scheduleId]
    );
  };

  const selectAllSchedules = () => {
    const scheduleIds = sortedSchedules.map(s => s.id);
    const allSelected = scheduleIds.every(id => selectedSchedules.includes(id));
    if (allSelected) {
      setSelectedSchedules([]);
    } else {
      setSelectedSchedules(scheduleIds);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedSchedules.length === 0) return;
    
    setDeleting(true);
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const id of selectedSchedules) {
        try {
          await axios.delete(`${API}/work/schedules/${id}`, { headers: getAuthHeaders() });
          successCount++;
        } catch (error) {
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} iş programı silindi`);
        loadSchedules();
      }
      if (failCount > 0) {
        toast.error(`${failCount} öğe silinemedi`);
      }
      
      setSelectedSchedules([]);
      setShowDeleteModal(false);
    } catch (error) {
      toast.error('Silme işlemi sırasında hata oluştu');
    } finally {
      setDeleting(false);
    }
  };

  // Sıralanmış veriler
  const sortedSchedules = React.useMemo(() => {
    if (!sortConfig.key) return schedules;
    
    return [...schedules].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'title':
          aValue = a.title?.toLowerCase() || '';
          bValue = b.title?.toLowerCase() || '';
          break;
        case 'owner':
          aValue = (a.owner?.name || a.owner_name || '').toLowerCase();
          bValue = (b.owner?.name || b.owner_name || '').toLowerCase();
          break;
        case 'created_at':
          aValue = new Date(a.created_at || 0).getTime();
          bValue = new Date(b.created_at || 0).getTime();
          break;
        case 'accepted_by':
          aValue = a.accepted_by?.length || 0;
          bValue = b.accepted_by?.length || 0;
          break;
        case 'start_date':
          aValue = new Date(a.start_date || 0).getTime();
          bValue = new Date(b.start_date || 0).getTime();
          break;
        case 'priority':
          const priorityOrder = { high: 3, normal: 2, low: 1 };
          aValue = priorityOrder[a.priority] || 0;
          bValue = priorityOrder[b.priority] || 0;
          break;
        case 'status':
          const statusOrder = { completed: 4, in_progress: 3, accepted: 2, pending: 1 };
          aValue = statusOrder[a.status] || 0;
          bValue = statusOrder[b.status] || 0;
          break;
        case 'completed_at':
          aValue = a.completed_at ? new Date(a.completed_at).getTime() : 0;
          bValue = b.completed_at ? new Date(b.completed_at).getTime() : 0;
          break;
        case 'updated_at':
          aValue = a.updated_at ? new Date(a.updated_at).getTime() : 0;
          bValue = b.updated_at ? new Date(b.updated_at).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [schedules, sortConfig]);

  const isPreviewable = (type) => {
    return type && (type.startsWith('image/') || type === 'application/pdf');
  };

  const handleCreateSchedule = async () => {
    if (!newSchedule.title) {
      toast.error('İş planı başlığı gerekli');
      return;
    }
    
    try {
      // send_to_users seçilen kişiler hem gönderilecek hem görebilecek kişiler olsun
      const scheduleData = {
        ...newSchedule,
        schedule_type: mainTab,
        owner_name: user?.name || 'Bilinmiyor',
        visible_to_users: [...new Set([...(newSchedule.visible_to_users || []), ...(newSchedule.send_to_users || [])])]
      };
      await axios.post(`${API}/work/schedules`, scheduleData, { headers: getAuthHeaders() });
      toast.success('İş planı oluşturuldu');
      setCreateModalOpen(false);
      setNewSchedule({
        title: '',
        description: '',
        schedule_type: mainTab,
        start_date: '',
        end_date: '',
        assigned_to: [],
        priority: 'normal',
        documents: [],
        send_to_users: [],
        visible_to_users: []
      });
      loadSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İş planı oluşturulamadı');
    }
  };

  const handleUpdateSchedule = async () => {
    if (!selectedSchedule) return;
    
    try {
      await axios.put(`${API}/work/schedules/${selectedSchedule.id}`, {
        title: selectedSchedule.title,
        description: selectedSchedule.description,
        start_date: selectedSchedule.start_date,
        end_date: selectedSchedule.end_date,
        priority: selectedSchedule.priority,
        status: selectedSchedule.status,
        visible_to_users: selectedSchedule.visible_to_users || []
      }, { headers: getAuthHeaders() });
      toast.success('İş planı güncellendi');
      setEditModalOpen(false);
      loadSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İş planı güncellenemedi');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Bu iş planını silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API}/work/schedules/${scheduleId}`, { headers: getAuthHeaders() });
      toast.success('İş planı silindi');
      loadSchedules();
      setDetailModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İş planı silinemedi');
    }
  };

  const handleAcceptSchedule = async (scheduleId) => {
    try {
      await axios.post(`${API}/work/schedules/${scheduleId}/accept`, {}, { headers: getAuthHeaders() });
      toast.success('İş planı kabul edildi');
      loadSchedules();
    } catch (error) {
      toast.error('İş planı kabul edilemedi');
    }
  };

  const loadProgressLogs = async (scheduleId) => {
    try {
      const res = await axios.get(`${API}/work/schedules/${scheduleId}/progress`, { headers: getAuthHeaders() });
      setProgressLogs(res.data.progress_logs || []);
    } catch (error) {
      console.error('Güncel durum yüklenemedi:', error);
    }
  };

  const handleOpenProgressModal = (schedule) => {
    setSelectedSchedule(schedule);
    loadProgressLogs(schedule.id);
    setProgressModalOpen(true);
  };

  const handleProgressDocUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setNewProgressLog(prev => ({
          ...prev,
          documents: [...prev.documents, {
            name: file.name,
            type: file.type,
            size: file.size,
            data: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleAddProgressLog = async () => {
    if (!newProgressLog.description.trim()) {
      toast.error('Açıklama gerekli');
      return;
    }
    try {
      await axios.post(`${API}/work/schedules/${selectedSchedule.id}/progress`, newProgressLog, { headers: getAuthHeaders() });
      toast.success('Güncel durum eklendi');
      setNewProgressLog({ description: '', documents: [] });
      loadProgressLogs(selectedSchedule.id);
      loadSchedules();
    } catch (error) {
      toast.error('Güncel durum eklenemedi');
    }
  };

  const handleStatusChange = async (scheduleId, newStatus) => {
    try {
      await axios.put(`${API}/work/schedules/${scheduleId}`, { status: newStatus }, { headers: getAuthHeaders() });
      toast.success('Durum güncellendi');
      loadSchedules();
    } catch (error) {
      toast.error('Durum güncellenemedi');
    }
  };

  const handleOpenCompletionModal = (schedule) => {
    setSelectedSchedule(schedule);
    setCompletionData({
      completion_notes: '',
      completion_date: new Date().toISOString().split('T')[0]
    });
    setDetailModalOpen(false);
    setCompletionModalOpen(true);
  };

  const handleCompleteWithDetails = async () => {
    if (!selectedSchedule) return;
    
    if (!completionData.completion_notes.trim()) {
      toast.error('Tamamlama açıklaması gerekli');
      return;
    }
    
    try {
      const completionDateTime = new Date().toISOString();
      
      await axios.put(`${API}/work/schedules/${selectedSchedule.id}`, {
        status: 'completed',
        completion_notes: completionData.completion_notes,
        completion_date: completionDateTime,
        completed_by: user?.id,
        completed_by_name: user?.name
      }, { headers: getAuthHeaders() });
      toast.success('İş planı tamamlandı');
      setCompletionModalOpen(false);
      loadSchedules();
    } catch (error) {
      toast.error('İş planı tamamlanamadı');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      planned: { label: 'Planlandı', color: 'bg-blue-100 text-blue-700', icon: Clock },
      in_progress: { label: 'Devam Ediyor', color: 'bg-amber-100 text-amber-700', icon: RefreshCw },
      completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      cancelled: { label: 'İptal Edildi', color: 'bg-red-100 text-red-700', icon: XCircle }
    };
    const c = config[status] || config.planned;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} gap-1`}>
        <Icon className="h-3 w-3" />
        {c.label}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { label: 'Düşük', color: 'bg-gray-100 text-gray-600' },
      normal: { label: 'Normal', color: 'bg-blue-100 text-blue-600' },
      high: { label: 'Yüksek', color: 'bg-orange-100 text-orange-600' },
      urgent: { label: 'Acil', color: 'bg-red-100 text-red-600' }
    };
    const c = config[priority] || config.normal;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const getTabIcon = (type) => {
    switch(type) {
      case 'daily': return CalendarDays;
      case 'monthly': return CalendarRange;
      case 'master': return Layers;
      default: return Calendar;
    }
  };

  const getTabLabel = (type) => {
    switch(type) {
      case 'daily': return 'GÜNLÜK İŞ PLANI';
      case 'monthly': return 'AYLIK İŞ PLANI';
      case 'master': return 'MASTER İŞ PLANI';
      default: return type;
    }
  };

  const renderScheduleTable = () => {
    if (schedules.length === 0) {
      return (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="h-16 w-16 mx-auto mb-4 text-gray-200" />
            <h3 className="text-xl font-bold mb-2">İş Planı Bulunmuyor</h3>
            <p className="text-gray-500">Bu kategoride iş planı yok</p>
            <Button 
              onClick={() => setCreateModalOpen(true)}
              className="mt-4 gap-2 bg-amber-600 hover:bg-amber-700"
            >
              <Plus className="h-4 w-4" />
              İş Planı Ekle
            </Button>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent className="p-0">
          {/* Seçim Toolbar */}
          <div className="flex items-center justify-between p-3 border-b">
            <div className="flex items-center gap-3">
              <Checkbox
                checked={sortedSchedules.length > 0 && sortedSchedules.every(s => selectedSchedules.includes(s.id))}
                onCheckedChange={selectAllSchedules}
              />
              <span className="text-sm text-gray-500">
                {selectedSchedules.length > 0 ? `${selectedSchedules.length} seçili` : 'Tümünü seç'}
              </span>
            </div>
            {selectedSchedules.length > 0 && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setSelectedSchedules([])}>
                  Seçimi Temizle
                </Button>
                <Button variant="destructive" size="sm" onClick={() => setShowDeleteModal(true)} className="gap-1">
                  <Trash2 className="h-4 w-4" />
                  Sil ({selectedSchedules.length})
                </Button>
              </div>
            )}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('title')}
                >
                  <div className="flex items-center gap-1">
                    İş Planı
                    <SortIcon columnKey="title" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('owner')}
                >
                  <div className="flex items-center gap-1">
                    Oluşturan
                    <SortIcon columnKey="owner" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center gap-1">
                    Oluşturulma
                    <SortIcon columnKey="created_at" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('accepted_by')}
                >
                  <div className="flex items-center gap-1">
                    Kabul Edenler
                    <SortIcon columnKey="accepted_by" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('start_date')}
                >
                  <div className="flex items-center gap-1">
                    Tarih Aralığı
                    <SortIcon columnKey="start_date" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('priority')}
                >
                  <div className="flex items-center gap-1">
                    Öncelik
                    <SortIcon columnKey="priority" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Durum
                    <SortIcon columnKey="status" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('updated_at')}
                >
                  <div className="flex items-center gap-1">
                    Son Güncelleme
                    <SortIcon columnKey="updated_at" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('completed_at')}
                >
                  <div className="flex items-center gap-1">
                    Tamamlanma Zamanı
                    <SortIcon columnKey="completed_at" />
                  </div>
                </TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedSchedules.map(schedule => {
                const isUnread = isScheduleUnread(schedule);
                return (
                <TableRow 
                  key={schedule.id} 
                  data-testid={`schedule-row-${schedule.id}`}
                  className={`${selectedSchedules.includes(schedule.id) ? 'bg-amber-50' : ''} ${isUnread ? 'bg-orange-50' : ''}`}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedSchedules.includes(schedule.id)}
                      onCheckedChange={() => toggleScheduleSelection(schedule.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className={`font-medium ${isUnread ? 'text-blue-900' : ''}`}>{schedule.title}</p>
                        {isUnread && (
                          <Badge className="bg-orange-500 text-white text-xs animate-pulse">YENİ</Badge>
                        )}
                      </div>
                      {schedule.description && (
                        <p className="text-sm text-gray-500 truncate max-w-[200px]">{schedule.description}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      {schedule.owner?.name || schedule.owner_name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-gray-600">
                      {schedule.created_at 
                        ? new Date(schedule.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    {schedule.accepted_by && schedule.accepted_by.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {schedule.accepted_by.map((a, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-green-50 text-green-700">
                            {a.user_name}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {schedule.start_date ? (
                      <div className="text-sm">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-gray-400" />
                          {new Date(schedule.start_date).toLocaleDateString('tr-TR')}
                        </div>
                        {schedule.end_date && (
                          <div className="text-gray-500">
                            → {new Date(schedule.end_date).toLocaleDateString('tr-TR')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>{getPriorityBadge(schedule.priority)}</TableCell>
                  <TableCell>{getStatusBadge(schedule.status)}</TableCell>
                  <TableCell>
                    {schedule.updated_at ? (
                      <div className="text-sm text-blue-600 font-medium">
                        {new Date(schedule.updated_at).toLocaleDateString('tr-TR', { 
                          day: '2-digit', month: '2-digit', year: 'numeric', 
                          hour: '2-digit', minute: '2-digit' 
                        })}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {schedule.status === 'completed' && schedule.completion_date ? (
                      <div className="text-sm text-green-600">
                        {new Date(schedule.completion_date).toLocaleDateString('tr-TR', { 
                          day: '2-digit', month: '2-digit', year: 'numeric', 
                          hour: '2-digit', minute: '2-digit' 
                        })}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => { 
                          setSelectedSchedule(schedule); 
                          setDetailModalOpen(true);
                          // YENİ badge için okundu işaretle
                          if (isUnread) {
                            markScheduleAsRead(schedule.id);
                          }
                        }}
                        data-testid={`view-schedule-${schedule.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {schedule.owner_id === user?.id && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => { setSelectedSchedule({...schedule}); setEditModalOpen(true); }}
                            data-testid={`edit-schedule-${schedule.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            title="Arşivle"
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                            onClick={() => handleArchiveSchedule(schedule.id)}
                            data-testid={`archive-schedule-${schedule.id}`}
                          >
                            <Archive className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="branch-schedule">
      {/* Bilgilendirme Notu */}
      <InfoBanner id="branch-assignments-info" variant="info">
        <strong>İş Programı</strong> bölümünde <strong>GÜNLÜK İŞ PLANI</strong>, <strong>AYLIK İŞ PLANI</strong>, <strong>MASTER İŞ PLANLARINIZI</strong> ekleyebilirsiniz. 
        İstediğiniz kişileri İş Programına İş Planı eklerken dahil edebilirsiniz. 
        İş Programlarına güncel durum ekleyerek veya güncel durumlarını kontrol ederek son durumlarını bildirebilir ve öğrenebilirsiniz.
      </InfoBanner>

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">İŞ PROGRAMI</h1>
          <p className="text-gray-500">Günlük, aylık ve master iş planlarınızı yönetin</p>
        </div>
        <Button onClick={loadSchedules} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Yenile
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      ) : (
        <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-4">
            {['daily', 'monthly', 'master'].map(type => {
              const Icon = getTabIcon(type);
              const unreadCount = type === 'daily' ? unreadDailyCount 
                : type === 'monthly' ? unreadMonthlyCount 
                : type === 'master' ? unreadMasterCount 
                : 0;
              return (
                <TabsTrigger 
                  key={type} 
                  value={type} 
                  className="gap-2"
                  data-testid={`tab-${type}`}
                >
                  <Icon className="h-4 w-4" />
                  {getTabLabel(type)}
                  {unreadCount > 0 && (
                    <Badge className="ml-1 bg-orange-500 text-white animate-pulse">{unreadCount} yeni</Badge>
                  )}
                </TabsTrigger>
              );
            })}
            <TabsTrigger 
              value="archive" 
              className="gap-2"
              data-testid="tab-archive"
            >
              <Archive className="h-4 w-4" />
              ARŞİV ({archivedSchedules.length})
            </TabsTrigger>
          </TabsList>

          {/* GÜNLÜK İŞ PLANI */}
          <TabsContent value="daily" className="mt-4">
            <div className="flex justify-end mb-4">
              <Button 
                onClick={() => setCreateModalOpen(true)}
                className="gap-2 bg-amber-600 hover:bg-amber-700"
                data-testid="add-daily-schedule-btn"
              >
                <Plus className="h-4 w-4" />
                İŞ PLANI EKLE
              </Button>
            </div>
            {renderScheduleTable()}
          </TabsContent>

          {/* AYLIK İŞ PLANI */}
          <TabsContent value="monthly" className="mt-4">
            <div className="flex justify-end mb-4">
              <Button 
                onClick={() => setCreateModalOpen(true)}
                className="gap-2 bg-amber-600 hover:bg-amber-700"
                data-testid="add-monthly-schedule-btn"
              >
                <Plus className="h-4 w-4" />
                İŞ PLANI EKLE
              </Button>
            </div>
            {renderScheduleTable()}
          </TabsContent>

          {/* MASTER İŞ PLANI */}
          <TabsContent value="master" className="mt-4">
            <div className="flex justify-end mb-4">
              <Button 
                onClick={() => setCreateModalOpen(true)}
                className="gap-2 bg-amber-600 hover:bg-amber-700"
                data-testid="add-master-schedule-btn"
              >
                <Plus className="h-4 w-4" />
                İŞ PLANI EKLE
              </Button>
            </div>
            {renderScheduleTable()}
          </TabsContent>

          {/* ARŞİV */}
          <TabsContent value="archive" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-gray-600">
                  <Archive className="h-5 w-5" />
                  Arşivlenmiş İş Planları
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {archivedSchedules.length === 0 ? (
                  <div className="text-center py-12">
                    <Archive className="h-16 w-16 mx-auto mb-4 text-gray-200" />
                    <h3 className="text-xl font-bold mb-2 text-gray-600">Arşivde İş Planı Yok</h3>
                    <p className="text-gray-500">Arşivlenmiş iş planı bulunmuyor</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-bold">İş Planı</TableHead>
                        <TableHead className="font-bold">Tür</TableHead>
                        <TableHead className="font-bold">Oluşturan</TableHead>
                        <TableHead className="font-bold">Durum</TableHead>
                        <TableHead className="font-bold">Arşiv Tarihi</TableHead>
                        <TableHead className="font-bold">İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {archivedSchedules.map(schedule => (
                        <TableRow key={schedule.id} className="hover:bg-gray-50">
                          <TableCell>
                            <div className="font-medium">{schedule.title}</div>
                            {schedule.description && (
                              <div className="text-sm text-gray-500 truncate max-w-[200px]">{schedule.description}</div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {schedule.schedule_type === 'daily' ? 'Günlük' : schedule.schedule_type === 'monthly' ? 'Aylık' : 'Master'}
                            </Badge>
                          </TableCell>
                          <TableCell>{schedule.owner?.name || schedule.owner_name || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-gray-500">
                              {schedule.status || 'Bilinmiyor'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {schedule.archived_at ? new Date(schedule.archived_at).toLocaleDateString('tr-TR', {
                              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            }) : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                title="Görüntüle"
                                onClick={() => { setSelectedSchedule(schedule); setDetailModalOpen(true); }}
                                data-testid={`view-archived-schedule-${schedule.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                title="Geri Yükle"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => handleRestoreSchedule(schedule.id)}
                                data-testid={`restore-schedule-${schedule.id}`}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* İş Planı Oluşturma Modalı */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-amber-600" />
              Yeni İş Planı Ekle - {getTabLabel(mainTab)}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="schedule-title">İş Planı Başlığı *</Label>
              <Input 
                id="schedule-title"
                value={newSchedule.title}
                onChange={(e) => setNewSchedule({ ...newSchedule, title: e.target.value })}
                placeholder="İş planı başlığını girin"
                data-testid="schedule-title-input"
              />
            </div>
            
            <div>
              <Label htmlFor="schedule-desc">Açıklama</Label>
              <Textarea 
                id="schedule-desc"
                value={newSchedule.description}
                onChange={(e) => setNewSchedule({ ...newSchedule, description: e.target.value })}
                placeholder="İş planı açıklaması"
                rows={3}
                data-testid="schedule-desc-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="schedule-start">Başlangıç Tarihi</Label>
                <Input 
                  id="schedule-start"
                  type="date"
                  value={newSchedule.start_date}
                  onChange={(e) => setNewSchedule({ ...newSchedule, start_date: e.target.value })}
                  data-testid="schedule-start-input"
                />
              </div>
              
              <div>
                <Label htmlFor="schedule-end">Bitiş Tarihi</Label>
                <Input 
                  id="schedule-end"
                  type="date"
                  value={newSchedule.end_date}
                  onChange={(e) => setNewSchedule({ ...newSchedule, end_date: e.target.value })}
                  data-testid="schedule-end-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="schedule-priority">Öncelik</Label>
              <Select 
                value={newSchedule.priority} 
                onValueChange={(val) => setNewSchedule({ ...newSchedule, priority: val })}
              >
                <SelectTrigger data-testid="schedule-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Düşük</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">Yüksek</SelectItem>
                  <SelectItem value="urgent">Acil</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Belge Yükleme */}
            <div className="border rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Upload className="h-4 w-4 text-blue-500" />
                  Belgeler
                </Label>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleDocumentUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  />
                  <span className="text-sm text-blue-600 hover:underline">+ Belge Ekle</span>
                </label>
              </div>
              
              {newSchedule.documents.length > 0 && (
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {newSchedule.documents.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{doc.name}</span>
                        <span className="text-xs text-gray-400">({formatFileSize(doc.size)})</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                        onClick={() => removeDocument(idx)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Şube Çalışanlarına Gönder */}
            <div className="border rounded-lg p-3 space-y-3">
              <Label className="flex items-center gap-2">
                <Send className="h-4 w-4 text-green-500" />
                Kişilere Gönder (Opsiyonel)
              </Label>
              <ScrollArea className="h-32">
                <div className="space-y-1">
                  {users.map(u => (
                    <div
                      key={u.id}
                      className={`flex items-center justify-between p-2 rounded cursor-pointer ${
                        newSchedule.send_to_users.includes(u.id) ? 'bg-green-100' : 'hover:bg-gray-100'
                      }`}
                      onClick={() => toggleUserSelection(u.id)}
                    >
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">{u.name}</span>
                      </div>
                      {newSchedule.send_to_users.includes(u.id) && (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      )}
                    </div>
                  ))}
                  {users.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-2">Şubede kullanıcı yok</p>
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleCreateSchedule}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="submit-schedule-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              İş Planı Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İş Planı Detay Modalı */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-amber-600" />
              İş Planı Detayı
            </DialogTitle>
          </DialogHeader>
          
          {selectedSchedule && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-amber-100 text-amber-700">
                    {getTabLabel(selectedSchedule.schedule_type)}
                  </Badge>
                  {getStatusBadge(selectedSchedule.status)}
                </div>
                <h3 className="font-bold text-lg mb-2">{selectedSchedule.title}</h3>
                {selectedSchedule.description && (
                  <p className="text-gray-600">{selectedSchedule.description}</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Oluşturan</Label>
                  <p className="font-medium">{selectedSchedule.owner?.name || selectedSchedule.owner_name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Oluşturulma Tarihi</Label>
                  <p className="font-medium">
                    {selectedSchedule.created_at 
                      ? new Date(selectedSchedule.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Öncelik</Label>
                  <div className="mt-1">{getPriorityBadge(selectedSchedule.priority)}</div>
                </div>
                {selectedSchedule.start_date && (
                  <div>
                    <Label className="text-gray-500">Başlangıç</Label>
                    <p className="font-medium">{new Date(selectedSchedule.start_date).toLocaleDateString('tr-TR')}</p>
                  </div>
                )}
                {selectedSchedule.end_date && (
                  <div>
                    <Label className="text-gray-500">Bitiş</Label>
                    <p className="font-medium">{new Date(selectedSchedule.end_date).toLocaleDateString('tr-TR')}</p>
                  </div>
                )}
              </div>

              {/* Arşiv Bilgileri */}
              {selectedSchedule.is_archived && (
                <div className="pt-4 border-t bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Archive className="h-4 w-4 text-orange-600" />
                    <span className="font-semibold text-orange-700">Arşiv Bilgileri</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-gray-500">Arşivleyen</Label>
                      <p className="font-medium">{selectedSchedule.archived_by_name || selectedSchedule.archived_by || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Arşiv Tarihi</Label>
                      <p className="font-medium">
                        {selectedSchedule.archived_at 
                          ? new Date(selectedSchedule.archived_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : '-'}
                      </p>
                    </div>
                  </div>
                  {selectedSchedule.archive_reason && (
                    <div className="mt-3">
                      <Label className="text-gray-500">Arşiv Nedeni</Label>
                      <p className="font-medium text-orange-800 bg-orange-100 p-2 rounded mt-1">{selectedSchedule.archive_reason}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Belgeler */}
              {selectedSchedule.documents && selectedSchedule.documents.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">Ekli Belgeler</Label>
                  <div className="space-y-2">
                    {selectedSchedule.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-blue-500" />
                          <div>
                            <p className="font-medium text-sm">{doc.name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(doc.size)}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {isPreviewable(doc.type) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPreviewDoc(doc)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = doc.data;
                              link.download = doc.name;
                              link.click();
                            }}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Kabul Edenler */}
              {selectedSchedule.accepted_by && selectedSchedule.accepted_by.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">Kabul Edenler</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedSchedule.accepted_by.map((a, idx) => (
                      <Badge key={idx} className="bg-green-100 text-green-700">
                        {a.user_name} - {new Date(a.accepted_at).toLocaleDateString('tr-TR')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Durum Değiştirme - Aktif planlar için */}
              {selectedSchedule.status !== 'completed' && selectedSchedule.status !== 'cancelled' && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">İşlemler</Label>
                  <div className="flex gap-2 flex-wrap">
                    {selectedSchedule.status === 'planned' && (
                      <Button 
                        onClick={() => {
                          handleAcceptSchedule(selectedSchedule.id);
                          setDetailModalOpen(false);
                        }}
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Başlat
                      </Button>
                    )}
                    
                    <Button 
                      onClick={() => {
                        setDetailModalOpen(false);
                        handleOpenProgressModal(selectedSchedule);
                      }}
                      variant="outline"
                      className="border-blue-500 text-blue-600 hover:bg-blue-50"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Güncel Durum
                    </Button>
                    
                    {selectedSchedule.owner_id === user?.id && selectedSchedule.status === 'in_progress' && (
                      <>
                        <Button 
                          onClick={() => handleOpenCompletionModal(selectedSchedule)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Tamamla
                        </Button>
                        <Button 
                          onClick={() => handleStatusChange(selectedSchedule.id, 'cancelled')}
                          variant="destructive"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          İptal Et
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* İş Planı Düzenleme Modalı */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-amber-600" />
              İş Planı Düzenle
            </DialogTitle>
          </DialogHeader>
          
          {selectedSchedule && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-title">İş Planı Başlığı</Label>
                <Input 
                  id="edit-title"
                  value={selectedSchedule.title}
                  onChange={(e) => setSelectedSchedule({ ...selectedSchedule, title: e.target.value })}
                  data-testid="edit-title-input"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-desc">Açıklama</Label>
                <Textarea 
                  id="edit-desc"
                  value={selectedSchedule.description || ''}
                  onChange={(e) => setSelectedSchedule({ ...selectedSchedule, description: e.target.value })}
                  rows={3}
                  data-testid="edit-desc-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-start">Başlangıç Tarihi</Label>
                  <Input 
                    id="edit-start"
                    type="date"
                    value={selectedSchedule.start_date || ''}
                    onChange={(e) => setSelectedSchedule({ ...selectedSchedule, start_date: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-end">Bitiş Tarihi</Label>
                  <Input 
                    id="edit-end"
                    type="date"
                    value={selectedSchedule.end_date || ''}
                    onChange={(e) => setSelectedSchedule({ ...selectedSchedule, end_date: e.target.value })}
                  />
                </div>
              </div>
              
              <div>
                <Label>Öncelik</Label>
                <Select 
                  value={selectedSchedule.priority} 
                  onValueChange={(val) => setSelectedSchedule({ ...selectedSchedule, priority: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Düşük</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">Yüksek</SelectItem>
                    <SelectItem value="urgent">Acil</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Görüntüleyebilecek Kişiler */}
              <div>
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Görüntüleyebilecek Kişiler
                </Label>
                <p className="text-xs text-gray-500 mb-2">Bu iş planını görebilecek kullanıcıları seçin. Boş bırakılırsa herkes görebilir.</p>
                <ScrollArea className="h-32 border rounded-md p-2">
                  {users.map(u => (
                    <div key={u.id} className="flex items-center gap-2 py-1">
                      <Checkbox 
                        checked={(selectedSchedule.visible_to_users || []).includes(u.id)}
                        onCheckedChange={(checked) => {
                          const current = selectedSchedule.visible_to_users || [];
                          setSelectedSchedule({
                            ...selectedSchedule,
                            visible_to_users: checked 
                              ? [...current, u.id]
                              : current.filter(id => id !== u.id)
                          });
                        }}
                      />
                      <span className="text-sm">{u.name}</span>
                    </div>
                  ))}
                </ScrollArea>
                {(selectedSchedule.visible_to_users || []).length > 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    {(selectedSchedule.visible_to_users || []).length} kişi seçildi
                  </p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleUpdateSchedule}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="save-schedule-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Belge Önizleme Modal */}
      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-500" />
              {previewDoc?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4 bg-gray-100 rounded-lg min-h-[400px] max-h-[70vh] overflow-auto">
            {previewDoc?.type?.startsWith('image/') ? (
              <img 
                src={previewDoc?.data} 
                alt={previewDoc?.name}
                className="max-w-full max-h-[65vh] object-contain rounded shadow-lg"
              />
            ) : previewDoc?.type === 'application/pdf' ? (
              <iframe
                src={previewDoc?.data}
                title={previewDoc?.name}
                className="w-full h-[65vh] border-0 rounded"
              />
            ) : (
              <p className="text-gray-500">Bu dosya türü önizlenemiyor</p>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                const link = document.createElement('a');
                link.href = previewDoc?.data;
                link.download = previewDoc?.name;
                link.click();
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              İndir
            </Button>
            <Button onClick={() => setPreviewDoc(null)}>
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Güncel Durum Modal */}
      <Dialog open={progressModalOpen} onOpenChange={setProgressModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              Güncel Durum - {selectedSchedule?.title}
            </DialogTitle>
          </DialogHeader>
          
          {selectedSchedule?.status !== 'completed' && selectedSchedule?.status !== 'cancelled' && (
            <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <Plus className="h-5 w-5 text-green-600" />
                <Label className="font-semibold">Yeni Güncelleme Ekle</Label>
              </div>
              <Textarea
                value={newProgressLog.description}
                onChange={(e) => setNewProgressLog(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ne yapıldı? Açıklama yazın..."
                rows={3}
              />
              <div className="flex items-center justify-between">
                <label className="cursor-pointer flex items-center gap-2 text-sm text-blue-600 hover:underline">
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleProgressDocUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  />
                  <Upload className="h-4 w-4" />
                  Belge/Fotoğraf Ekle
                </label>
                <Button onClick={handleAddProgressLog} className="bg-green-600 hover:bg-green-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Ekle
                </Button>
              </div>
              {newProgressLog.documents.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {newProgressLog.documents.map((doc, idx) => (
                    <Badge key={idx} variant="outline" className="gap-1">
                      <FileText className="h-3 w-3" />
                      {doc.name}
                      <button
                        onClick={() => setNewProgressLog(prev => ({
                          ...prev,
                          documents: prev.documents.filter((_, i) => i !== idx)
                        }))}
                        className="ml-1 text-red-500 hover:text-red-700"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="space-y-4 mt-4">
            <Label className="font-semibold">Güncelleme Geçmişi</Label>
            {progressLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>Henüz güncelleme yapılmamış</p>
              </div>
            ) : (
              <div className="space-y-3">
                {progressLogs.map((log) => (
                  <div key={log.id} className="border rounded-lg p-4 bg-white">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">{log.user_name}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(log.created_at).toLocaleDateString('tr-TR', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-gray-700 mb-2">{log.description}</p>
                    {log.documents && log.documents.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t">
                        {log.documents.map((doc, idx) => (
                          <div key={idx} className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1">
                            <FileText className="h-3 w-3 text-gray-500" />
                            <span className="text-xs">{doc.name}</span>
                            {isPreviewable(doc.type) && (
                              <button
                                onClick={() => setPreviewDoc(doc)}
                                className="text-blue-500 hover:text-blue-700 ml-1"
                              >
                                <Eye className="h-3 w-3" />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = doc.data;
                                link.download = doc.name;
                                link.click();
                              }}
                              className="text-green-500 hover:text-green-700 ml-1"
                            >
                              <Download className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Tamamlama Modal */}
      <Dialog open={completionModalOpen} onOpenChange={setCompletionModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              İş Planını Tamamla
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="font-medium">{selectedSchedule?.title}</p>
              <p className="text-sm text-gray-500">{selectedSchedule?.description}</p>
            </div>
            
            <div className="bg-blue-50 p-3 rounded-lg text-blue-700 text-sm">
              <p className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Tamamlanma tarihi ve saati otomatik olarak kaydedilecektir.
              </p>
            </div>
            
            <div>
              <Label htmlFor="completion-notes">Tamamlama Açıklaması *</Label>
              <Textarea 
                id="completion-notes"
                value={completionData.completion_notes}
                onChange={(e) => setCompletionData(prev => ({ ...prev, completion_notes: e.target.value }))}
                placeholder="İş planının nasıl tamamlandığını açıklayın..."
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompletionModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleCompleteWithDetails}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Tamamla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Silme Onay Modalı */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              İş Programlarını Sil
            </DialogTitle>
            <DialogDescription>
              {selectedSchedules.length} adet iş programını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
              İptal
            </Button>
            <Button variant="destructive" onClick={handleDeleteSelected} disabled={deleting}>
              {deleting ? 'Siliniyor...' : `Evet, ${selectedSchedules.length} Programı Sil`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Arşiv Açıklaması Modal */}
      <Dialog open={archiveReasonModalOpen} onOpenChange={setArchiveReasonModalOpen}>
        <DialogContent className="max-w-md" data-testid="archive-reason-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Archive className="h-5 w-5" />
              İş Planını Arşive Gönder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Bu iş planını arşive göndermek için bir açıklama yazmanız gerekmektedir.
            </p>
            <div className="space-y-2">
              <Label htmlFor="archive-reason" className="text-sm font-medium">
                Arşiv Açıklaması <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="archive-reason"
                placeholder="Arşivleme nedenini yazın..."
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                className="min-h-[100px]"
                data-testid="archive-reason-input"
              />
              <p className="text-xs text-gray-500">
                Bu açıklama arşivlenen öğenin detaylarında görünecektir.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setArchiveReasonModalOpen(false);
                setArchiveReason('');
                setArchiveTargetId(null);
              }}
              disabled={archivingItem}
            >
              İptal
            </Button>
            <Button 
              onClick={handleConfirmArchiveSchedule}
              className="bg-orange-600 hover:bg-orange-700"
              disabled={archivingItem || !archiveReason.trim()}
              data-testid="confirm-archive-btn"
            >
              {archivingItem ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Archive className="h-4 w-4 mr-2" />
              )}
              Arşive Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchAssignments;
